import React, { useEffect, useState, useCallback } from 'react';
import Layout from '../components/Layout';
import api from '../services/api';
import { FiCheck, FiX, FiClock, FiCalendar } from 'react-icons/fi';
import { getTodayIST, addDaysIST, formatDate } from '../utils/dateUtils';
import { useAuth } from '../context/AuthContext';
import { getServicesForDepartment } from '../utils/departmentMapping';

const ALL_SERVICES = [
  { id: 'Spa', name: 'Spa', timeRange: '7:00 AM - 10:00 PM' },
  { id: 'Yoga', name: 'Yoga', timeRange: '7:00 AM - 10:00 PM' },
  { id: 'Salon', name: 'Salon', timeRange: '7:00 AM - 10:00 PM' }
];

const DEFAULT_WORKING_HOURS = {
  Yoga: { start_time: '07:00', end_time: '09:00', duration_minutes: 60, capacity: 1 },
  Spa: { start_time: '09:00', end_time: '20:00', duration_minutes: 60, capacity: 1 },
  Salon: { start_time: '09:00', end_time: '20:00', duration_minutes: 60, capacity: 1 }
};

const getDefaultWorkingHours = (service) => {
  return DEFAULT_WORKING_HOURS[service] || { start_time: '07:00', end_time: '21:00', duration_minutes: 60, capacity: 1 };
};

function SlotManagement() {
  const { user } = useAuth();
  
  // Get available services based on user role
  const getAvailableServices = () => {
    // Admin and manager see all services
    if (user?.role === 'admin' || user?.role === 'manager') {
      return ALL_SERVICES;
    }
    
    // Department users see only their services
    if (user?.role === 'department' && user?.department) {
      const userServices = getServicesForDepartment(user.department);
      return ALL_SERVICES.filter(service => userServices.includes(service.id));
    }
    
    return ALL_SERVICES;
  };

  const availableServices = getAvailableServices();
  
  // Set default service based on user's department
  const getDefaultService = () => {
    if (user?.role === 'department' && user?.department) {
      const userServices = getServicesForDepartment(user.department);
      if (userServices.length > 0) {
        // Return first available service
        return userServices[0];
      }
    }
    return 'Yoga'; // Default for admin/manager
  };

  const [selectedService, setSelectedService] = useState('Yoga'); 
  const [workingHours, setWorkingHours] = useState(getDefaultWorkingHours('Yoga'));
  const [timeSlots, setTimeSlots] = useState({});
  const [dateSpecificTimeSlots, setDateSpecificTimeSlots] = useState({});
  const [daySlots, setDaySlots] = useState({});
  const [timeDisableMode, setTimeDisableMode] = useState('permanent');
  const [selectedDisableDate, setSelectedDisableDate] = useState(getTodayIST().toISOString().split('T')[0]);
  const [saveSuccessMessage, setSaveSuccessMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const disableModeLabel = timeDisableMode === 'date_specific' ? `Date-specific (${selectedDisableDate})` : 'Permanent (all days)';

  // Set default service when user loads and update if department changes
  useEffect(() => {
    if (user) {
      const defaultService = getDefaultService();
      if (defaultService !== selectedService) {
        setSelectedService(defaultService);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const generateTimeSlots = useCallback((hours) => {
    const slots = {};

    const effectiveHours = hours || { start_time: '07:00', end_time: '21:00', duration_minutes: 60 };
    const startHour = parseInt((effectiveHours.start_time || '07:00').split(':')[0], 10);
    const startMinute = parseInt((effectiveHours.start_time || '07:00').split(':')[1], 10);
    const endHour = parseInt((effectiveHours.end_time || '21:00').split(':')[0], 10);
    const endMinute = parseInt((effectiveHours.end_time || '21:00').split(':')[1], 10);
    const duration = parseInt(effectiveHours.duration_minutes || 60, 10);

    let current = startHour * 60 + startMinute;
    const end = endHour * 60 + endMinute;

    while (current + duration <= end) {
      const hour = Math.floor(current / 60);
      const minute = current % 60;
      const timeStr = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
      slots[timeStr] = { enabled: true, time: timeStr };
      current += duration;
    }

    return slots;
  }, []);

  const generateDaySlots = useCallback(() => {
    const slots = {};
    const today = getTodayIST();
    const startOffset = selectedService === 'Yoga' ? 1 : 0; // Yoga starts from tomorrow
    
    for (let i = startOffset; i < startOffset + 5; i++) {
      const date = addDaysIST(today, i);
      const dateStr = formatDate(date, 'yyyy-MM-dd');
      
      const dayName = formatDate(date, 'EEE');
      const month = formatDate(date, 'MMM');
      const day = date.getDate();
      
      slots[dateStr] = {
        enabled: true,
        date: dateStr,
        display: `${dayName}, ${month} ${day}`
      };
    }
    
    return slots;
  }, [selectedService]);

  const fetchSlotSettings = useCallback(async () => {
    try {
      setLoading(true);
      const defaultHours = getDefaultWorkingHours(selectedService);
      let effectiveWorkingHours = defaultHours;
      let savedTimeSlots = {};
      let savedDaySlots = {};
      
      // Fetch saved settings from API
      try {
        const response = await api.get(`/api/slots/${selectedService}?date=${selectedDisableDate}`);
        savedTimeSlots = response.data.timeSlots || {};
        savedDaySlots = response.data.daySlots || {};
        const savedDateSpecificTimeSlots = response.data.dateSpecificTimeSlots || {};
        effectiveWorkingHours = response.data.workingHours || defaultHours;
        setDateSpecificTimeSlots(savedDateSpecificTimeSlots);
      } catch (apiError) {
        // If API fails, just use defaults (slots are already generated)
        console.warn('Failed to fetch saved slot settings, using defaults:', apiError);
        setDateSpecificTimeSlots({});
      }

      setWorkingHours(effectiveWorkingHours);

      // Generate default slots using working hours and merge saved enabled flags
      const generatedTimeSlots = generateTimeSlots(effectiveWorkingHours);
      const mergedTimeSlots = { ...generatedTimeSlots };
      Object.keys(savedTimeSlots).forEach(time => {
        if (mergedTimeSlots[time]) {
          mergedTimeSlots[time].enabled = savedTimeSlots[time].enabled;
        }
      });
      setTimeSlots(mergedTimeSlots);

      const generatedDaySlots = generateDaySlots();
      const mergedDaySlots = { ...generatedDaySlots };
      Object.keys(savedDaySlots).forEach(date => {
        if (mergedDaySlots[date]) {
          mergedDaySlots[date].enabled = savedDaySlots[date].enabled;
        }
      });
      setDaySlots(mergedDaySlots);
    } catch (error) {
      console.error('Failed to fetch slot settings:', error);
    } finally {
      setLoading(false);
    }
  }, [generateTimeSlots, generateDaySlots, selectedService, selectedDisableDate]);

  useEffect(() => {
    fetchSlotSettings();
  }, [fetchSlotSettings]);

  const toggleTimeSlot = async (timeStr) => {
    try {
      if (timeDisableMode === 'date_specific') {
        const currentlyEnabled = dateSpecificTimeSlots[timeStr]?.enabled !== false;
        const newEnabled = !currentlyEnabled;

        setDateSpecificTimeSlots(prev => ({
          ...prev,
          [timeStr]: { ...(prev[timeStr] || { time: timeStr }), enabled: newEnabled }
        }));

        const response = await api.put(`/api/slots/${selectedService}/time-date`, {
          date: selectedDisableDate,
          time: timeStr,
          enabled: newEnabled
        });
        console.log('Date-specific time slot updated successfully:', response.data);
        return;
      }

      const newEnabled = !timeSlots[timeStr].enabled;

      // Update local state optimistically
      setTimeSlots(prev => ({
        ...prev,
        [timeStr]: { ...prev[timeStr], enabled: newEnabled }
      }));

      // Save to API
      const response = await api.put(`/api/slots/${selectedService}/time`, {
        time: timeStr,
        enabled: newEnabled
      });

      // Success - no need to do anything, state already updated
      console.log('Time slot updated successfully:', response.data);
    } catch (error) {
      console.error('Failed to update time slot:', error);
      const errorMessage = error.response?.data?.error || error.message || 'Unknown error';
      console.error('Error details:', errorMessage);

      if (timeDisableMode === 'date_specific') {
        setDateSpecificTimeSlots(prev => ({
          ...prev,
          [timeStr]: { ...(prev[timeStr] || { time: timeStr }), enabled: !(prev[timeStr]?.enabled !== false) }
        }));
      } else {
        // Revert on error
        setTimeSlots(prev => ({
          ...prev,
          [timeStr]: { ...prev[timeStr], enabled: !prev[timeStr].enabled }
        }));
      }
      alert(`Failed to save time slot setting: ${errorMessage}`);
    }
  };

  const toggleDaySlot = async (dateStr) => {
    try {
      const newEnabled = !daySlots[dateStr].enabled;
      
      // Update local state optimistically
      setDaySlots(prev => ({
        ...prev,
        [dateStr]: { ...prev[dateStr], enabled: newEnabled }
      }));
      
      // Save to API
      const response = await api.put(`/api/slots/${selectedService}/day`, { 
        date: dateStr, 
        enabled: newEnabled 
      });
      
      // Success - no need to do anything, state already updated
      console.log('Day slot updated successfully:', response.data);
    } catch (error) {
      console.error('Failed to update day slot:', error);
      const errorMessage = error.response?.data?.error || error.message || 'Unknown error';
      console.error('Error details:', errorMessage);
      
      // Revert on error
      setDaySlots(prev => ({
        ...prev,
        [dateStr]: { ...prev[dateStr], enabled: !prev[dateStr].enabled }
      }));
      alert(`Failed to save day slot setting: ${errorMessage}`);
    }
  };

  const saveWorkingHours = async () => {
    try {
      const startHour = parseInt((workingHours.start_time || '00:00').split(':')[0], 10);
      const endHour = parseInt((workingHours.end_time || '00:00').split(':')[0], 10);

      if ((workingHours.start_time || '').split(':')[1] !== '00' || (workingHours.end_time || '').split(':')[1] !== '00') {
        alert('Please select hours only (HH:00).');
        return;
      }

      if (startHour > endHour) {
        alert('Start hour must be earlier than or equal to end hour.');
        return;
      }

      const response = await api.put(`/api/slots/${selectedService}/working-hours`, {
        start_time: workingHours.start_time,
        end_time: workingHours.end_time,
        duration_minutes: parseInt(workingHours.duration_minutes || 60, 10),
        capacity: parseInt(workingHours.capacity || 1, 10)
      });

      const updatedWorkingHours = response.data;
      setWorkingHours(updatedWorkingHours);

      const generatedTimeSlots = generateTimeSlots(updatedWorkingHours);
      const mergedTimeSlots = { ...generatedTimeSlots };
      
      // Keep enabled states for slots that still exist
      Object.keys(timeSlots).forEach(time => {
        if (mergedTimeSlots[time]) {
          mergedTimeSlots[time].enabled = timeSlots[time].enabled;
        }
      });
      setTimeSlots(mergedTimeSlots);
      setSaveSuccessMessage('Changes saved successfully.');
      setTimeout(() => {
        setSaveSuccessMessage('');
      }, 2500);
    } catch (error) {
      console.error('Failed to update working hours:', error);
      const errorMessage = error.response?.data?.error || error.message || 'Unknown error';
      alert(`Failed to save working hours: ${errorMessage}`);
    }
  };

  const formatTime = (timeStr) => {
    const [hours, minutes] = timeStr.split(':');
    const hour = parseInt(hours);
    const period = hour < 12 ? 'AM' : 'PM';
    const displayHour = hour > 12 ? hour - 12 : (hour === 0 ? 12 : hour);
    return `${displayHour}:${minutes} ${period}`;
  };

  const timeSlotList = Object.values(timeSlots);
  const daySlotList = Object.values(daySlots);
  const enabledTimeSlots = timeSlotList.filter((slot) => {
    if (timeDisableMode === 'date_specific') {
      return dateSpecificTimeSlots[slot.time]?.enabled !== false;
    }
    return slot.enabled;
  }).length;
  const enabledDays = daySlotList.filter((slot) => slot.enabled).length;

  // Check if user has access to any services
  if (!loading && availableServices.length === 0 && user?.role === 'department') {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <p className="text-lg text-gray-600 mb-2">No slot management services available</p>
            <p className="text-sm text-gray-500">
              Your department ({user.department}) does not have access to slot management services.
            </p>
          </div>
        </div>
      </Layout>
    );
  }

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900"></div>
        </div>
      </Layout>
    );
  }


  return (
    <Layout>
      <div className="space-y-5 px-2 py-3 sm:px-4 sm:py-5 lg:px-4 lg:py-6 max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900 tracking-tight">
              {user?.role === 'department' && user?.department 
                ? `${user.department} Slot Management` 
                : 'Slot Management'}
            </h1>
            <p className="text-sm text-gray-500 mt-1">
              Configure session durations, working hours, and availability.
            </p>
          </div>
        </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="bg-white rounded-2xl border border-gray-100 p-6 shadow-sm">
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Service</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">{selectedService}</p>
              </div>
              <div className="bg-white rounded-2xl border border-gray-100 p-6 shadow-sm">
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Active Slots</p>
                <p className="text-2xl font-bold text-green-600 mt-1">{enabledTimeSlots}</p>
              </div>
              <div className="bg-white rounded-2xl border border-gray-100 p-6 shadow-sm">
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Enabled Days</p>
                <p className="text-2xl font-bold text-blue-600 mt-1">{enabledDays}</p>
              </div>
            </div>

            {/* Service Selection */}
            {availableServices.length > 1 && (
              <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide">
                {availableServices.map((service) => (
                  <button
                    key={service.id}
                    onClick={() => setSelectedService(service.id)}
                    className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all border ${
                      selectedService === service.id
                        ? 'bg-gray-900 text-white border-gray-900 shadow-md'
                        : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    {service.name}
                  </button>
                ))}
              </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Working Hours & Duration */}
              <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 space-y-6">
                <div className="flex items-center gap-2">
                  <div className="p-2 bg-blue-50 rounded-lg">
                    <FiClock className="h-5 w-5 text-blue-600" />
                  </div>
                  <h2 className="text-xl font-bold text-gray-900">Working Hours</h2>
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                  <div className="space-y-1.5">
                    <label className="text-xs font-semibold text-gray-500 uppercase">Start Time</label>
                    <input
                      type="time"
                      value={workingHours.start_time}
                      onChange={(e) => setWorkingHours(prev => ({ ...prev, start_time: e.target.value }))}
                      className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-xs font-semibold text-gray-500 uppercase">End Time</label>
                    <input
                      type="time"
                      value={workingHours.end_time}
                      onChange={(e) => setWorkingHours(prev => ({ ...prev, end_time: e.target.value }))}
                      className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-xs font-semibold text-gray-500 uppercase">Slot Duration</label>
                    <select
                      value={workingHours.duration_minutes || 60}
                      onChange={(e) => setWorkingHours(prev => ({ ...prev, duration_minutes: parseInt(e.target.value, 10) }))}
                      className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all bg-white"
                    >
                      <option value="30">30 Minutes</option>
                      <option value="45">45 Minutes</option>
                      <option value="60">1 Hour</option>
                      <option value="90">1.5 Hours</option>
                      <option value="120">2 Hours</option>
                      <option value="180">3 Hours</option>
                      <option value="240">4 Hours</option>
                    </select>
                  </div>

                </div>

                <button
                  onClick={saveWorkingHours}
                  className="w-full bg-blue-600 text-white py-3 rounded-xl font-bold hover:bg-blue-700 transition-colors shadow-lg shadow-blue-200"
                >
                  Apply & Save Changes
                </button>
                {saveSuccessMessage && (
                  <div className="w-full rounded-xl border border-green-200 bg-green-50 px-4 py-2.5 text-sm font-medium text-green-700">
                    {saveSuccessMessage}
                  </div>
                )}

                <div className="space-y-3">
                  <h3 className="text-sm font-bold text-gray-900">Manage Specific Time Slots</h3>
                  <div className="rounded-xl border border-blue-100 bg-blue-50 px-3 py-2">
                    <p className="text-xs text-blue-800">
                      <strong>Current mode:</strong> {disableModeLabel}. Click a slot to toggle availability for this mode only.
                    </p>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div className="space-y-1.5">
                      <label className="text-xs font-semibold text-gray-500 uppercase">Disable Mode</label>
                      <div className="grid grid-cols-2 gap-2">
                        <button
                          type="button"
                          onClick={() => setTimeDisableMode('permanent')}
                          className={`px-3 py-2 rounded-xl text-sm font-semibold border transition-all ${
                            timeDisableMode === 'permanent'
                              ? 'bg-gray-900 text-white border-gray-900'
                              : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50'
                          }`}
                        >
                          Permanent
                        </button>
                        <button
                          type="button"
                          onClick={() => setTimeDisableMode('date_specific')}
                          className={`px-3 py-2 rounded-xl text-sm font-semibold border transition-all ${
                            timeDisableMode === 'date_specific'
                              ? 'bg-gray-900 text-white border-gray-900'
                              : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50'
                          }`}
                        >
                          Particular Day
                        </button>
                      </div>
                    </div>
                    {timeDisableMode === 'date_specific' && (
                      <div className="space-y-1.5">
                        <label className="text-xs font-semibold text-gray-500 uppercase">Disable Date</label>
                        <input
                          type="date"
                          value={selectedDisableDate}
                          onChange={(e) => setSelectedDisableDate(e.target.value)}
                          className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all"
                        />
                        <div className="flex items-center gap-2">
                          {[0, 1, 2].map((offset) => {
                            const d = addDaysIST(getTodayIST(), offset);
                            const value = formatDate(d, 'yyyy-MM-dd');
                            const label = offset === 0 ? 'Today' : offset === 1 ? 'Tomorrow' : 'Day +2';
                            const active = selectedDisableDate === value;
                            return (
                              <button
                                key={value}
                                type="button"
                                onClick={() => setSelectedDisableDate(value)}
                                className={`px-2.5 py-1 rounded-full text-xs font-semibold border ${
                                  active ? 'bg-blue-100 text-blue-900 border-blue-300' : 'bg-white text-gray-600 border-gray-200'
                                }`}
                              >
                                {label}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-h-[400px] overflow-y-auto pr-1">
                    {timeSlotList.map((slot) => (
                      <button
                        key={slot.time}
                        onClick={() => toggleTimeSlot(slot.time)}
                        className={`flex items-center justify-between p-3 rounded-xl border transition-all text-left ${
                          (timeDisableMode === 'date_specific'
                            ? dateSpecificTimeSlots[slot.time]?.enabled !== false
                            : slot.enabled)
                            ? 'border-green-100 bg-green-50/50 text-green-900'
                            : 'border-gray-100 bg-gray-50 text-gray-400 opacity-75'
                        }`}
                      >
                        <span className="text-sm font-bold">{formatTime(slot.time)}</span>
                        {(timeDisableMode === 'date_specific'
                          ? dateSpecificTimeSlots[slot.time]?.enabled !== false
                          : slot.enabled) ? (
                          <div className="h-5 w-5 bg-green-500 rounded-full flex items-center justify-center text-white">
                            <FiCheck className="h-3 w-3" />
                          </div>
                        ) : (
                          <div className="h-5 w-5 bg-gray-300 rounded-full flex items-center justify-center text-white">
                            <FiX className="h-3 w-3" />
                          </div>
                        )}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Day Availability */}
              <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 space-y-6">
                <div className="flex items-center gap-2">
                  <div className="p-2 bg-purple-50 rounded-lg">
                    <FiCalendar className="h-5 w-5 text-purple-600" />
                  </div>
                  <h2 className="text-xl font-bold text-gray-900">Available Days</h2>
                </div>
                <p className="text-sm text-gray-500">
                  Enable or disable booking availability for specific dates.
                </p>

                <div className="space-y-3">
                  {daySlotList.map((slot) => (
                    <button
                      key={slot.date}
                      onClick={() => toggleDaySlot(slot.date)}
                      className={`flex items-center justify-between w-full p-4 rounded-2xl border transition-all ${
                        slot.enabled
                          ? 'border-purple-100 bg-purple-50/30 text-purple-900'
                          : 'border-gray-100 bg-gray-50 text-gray-400'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`h-10 w-10 rounded-xl flex items-center justify-center font-bold ${
                          slot.enabled ? 'bg-purple-100 text-purple-600' : 'bg-gray-200 text-gray-500'
                        }`}>
                          {slot.display.split(' ')[2]}
                        </div>
                        <div className="text-left">
                          <p className="font-bold">{slot.display.split(',')[0]}</p>
                          <p className="text-xs opacity-75">{slot.display.split(',')[1]}</p>
                        </div>
                      </div>
                      <div className={`px-4 py-1.5 rounded-full text-xs font-bold ${
                        slot.enabled ? 'bg-green-500 text-white' : 'bg-gray-300 text-white'
                      }`}>
                        {slot.enabled ? 'Enabled' : 'Disabled'}
                      </div>
                    </button>
                  ))}
                </div>

                <div className="p-4 bg-amber-50 rounded-2xl border border-amber-100">
                  <p className="text-xs text-amber-800 leading-relaxed">
                    <strong>Pro Tip:</strong> Disabling a day will hide all time slots for that specific date from the guest booking menu. Use this for maintenance or holidays.
                  </p>
                </div>
              </div>
            </div>
      </div>
    </Layout>
  );
}

export default SlotManagement;

